import {useAtom} from 'jotai';
import {
    PlaceOrderMutationVariables,
    SetGuestEmailOnCartMutationVariables,
    SetPaymentMethodOnCartMutationVariables,
    SetShippingMethodsOnCartMutationVariables,
    usePlaceOrderMutation,
    useSetGuestEmailOnCartMutation,
    useSetPaymentMethodOnCartMutation,
    useSetShippingMethodsOnCartMutation,
    useSetBillingAddressesOnCartMutation,
    useSetShippingAddressesOnCartMutation,
    useCartShippingAddressQuery,
    useAvailablePaymentMethodsQuery,
    useAvailableShippingMethodsQuery,
    SetShippingMethodsOnCartMutation,
    SetPaymentMethodOnCartMutation,
    useAvailableCouponsQuery,
    useApplyCouponToCartMutation,
    useGetAppliedCouponsQuery,
    useRemoveCouponFromCartMutation,
    useCustomerKicoinQuery,
    useGetDiscountKicoinsQuery,
    useRemoveKicoinsFromCartMutation,
    useApplyKicoinsToCartMutation,
    useCustomerCartLazyQuery,
    useCartTotalLazyQuery,
    useSelectedShippingMethodLazyQuery,
    useGetAppliedCouponsLazyQuery,
    useCartItemsStatusQuery,
} from './gql/cart.graphql';
import {availablePaymentMethodAtom, selectedPaymentMethodAtom} from '@/store/cart';
import {toast} from 'react-toastify';
import {AvailablePaymentMethod, CartAddressInput, SelectedPaymentMethod} from '__generated__/__types__';
import {useTranslation} from 'next-i18next';
import {ApolloError} from '@apollo/client';
import {useCartItemsLazyQuery} from '@/__generated__/src/framework/graphql/gql/cart.graphql';
import {getMagentoGraphqlApiEndpointPublic} from '@/framework/client';
import {logSentry} from '@/lib/sentry';
import {useMergeCartsMutation} from '@/framework/gql/cart-mutations.graphql';
import {useCallback, useEffect} from 'react';
import {
    hashCartToastMessage,
    INACTIVE_CART_MESSAGE,
    ORDER_ALREADY_PLACED,
} from '@/_application/modules/cart/models/error-message';

export function useCartId() {
    const [getCustomerCartQuery, {data, loading, error}] = useCustomerCartLazyQuery({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            logSentry({
                error: error,
                name: 'useCartId',
                transactionName: 'getCustomerCartQuery',
                extra: {err: error},
            });
        }
    }, [error]);

    return {getCustomerCartQuery, data, loading, error};
}

export function useSelectedShippingMethod() {
    const [getSelectedShippingMethodQuery, {data, loading, error}] = useSelectedShippingMethodLazyQuery();

    const getSelectedShippingMethod = async (cartId: string) => {
        const response = await getSelectedShippingMethodQuery({
            variables: {
                cartId: cartId,
            },
            fetchPolicy: 'no-cache',
        });
        return response.data?.cart?.shipping_addresses?.at(0)?.selected_shipping_method ?? {};
    };

    return {
        getSelectedShippingMethod,
        selectShippingMethod: data?.cart?.shipping_addresses?.at(0)?.selected_shipping_method,
        loading,
        error,
    };
}

export function useCartItems() {
    const [getCartItemsQuery, {data, loading, error}] = useCartItemsLazyQuery({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            logSentry({
                error: error,
                name: 'cart',
                transactionName: 'getCartItems',
                extra: {err: error},
            });
        }
    }, [error]);

    const getCartItems = useCallback(
        (options?: Parameters<typeof getCartItemsQuery>[0]) => {
            return getCartItemsQuery({
                ...options,
                context: {
                    uri: getMagentoGraphqlApiEndpointPublic(),
                    ...options?.context,
                },
            });
        },
        [getCartItemsQuery],
    );

    return {getCartItems, data, loading, error};
}

export function useCartItemsStatus(cartId: string) {
    const {data, loading, error} = useCartItemsStatusQuery({
        variables: {
            cartId,
        },
        // context: {
        //   uri: getMagentoGraphqlApiEndpointPublic(),
        // },
    });

    return {items: data?.cartItemsStatus ?? [], loading, error};
}

export function useCartTotal() {
    const [fetchCartTotalQuery, {data, loading, error}] = useCartTotalLazyQuery({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            logSentry({
                error: error,
                name: 'cart',
                transactionName: 'fetchCartTotalQuery',
                extra: {err: error},
            });
        }
    }, [error]);

    const fetchCartTotal = async (cartId: string) => {
        const response = await fetchCartTotalQuery({
            variables: {
                cartId,
            },
            context: {
                uri: getMagentoGraphqlApiEndpointPublic(),
            },
        });
        return response.data?.cart;
    };

    return {fetchCartTotal, data, loading, error};
}

export function useCartShippingAddress(cartId: string) {
    const {data, loading, error} = useCartShippingAddressQuery({
        variables: {
            cartId,
        },
        context: {
            uri: getMagentoGraphqlApiEndpointPublic(),
        },
        onCompleted: () => {},
        onError: err => {
            toast.error(err.message);
            logSentry({
                error: err,
                name: 'cart',
                transactionName: 'useCartShippingAddress',
                extra: {cartId},
            });
        },
    });

    return {data, loading, error};
}

export function useSetPaymentMethodOnCart() {
    const [setPaymentMethodsOnCartMutation, {loading, error}] = useSetPaymentMethodOnCartMutation();

    function setPaymentMethod(
        input: SetPaymentMethodOnCartMutationVariables['input'],
        onSuccess: (data: SetPaymentMethodOnCartMutation) => void,
        onError?: (err: ApolloError) => void,
    ) {
        return setPaymentMethodsOnCartMutation({
            // refetchQueries: ['SelectedPaymentMethod', 'CartTotal'],
            variables: {
                input,
            },
            onCompleted: data => {
                onSuccess(data);
            },
            onError: err => {
                if (error?.message !== INACTIVE_CART_MESSAGE) {
                    toast.error(err.message);
                }
                logSentry({
                    error: err,
                    name: 'cart',
                    transactionName: 'useSetPaymentMethodOnCart',
                    extra: {input},
                });
                onError && onError(err);
            },
        });
    }

    return {
        loading,
        error,
        setPaymentMethod,
    };
}

export function useSetShippingMethodOnCart() {
    const [setShippingMethodsOnCartMutation, {loading, error}] = useSetShippingMethodsOnCartMutation();

    function setShippingMethod(
        input: SetShippingMethodsOnCartMutationVariables['input'],
        onSuccess: (data: SetShippingMethodsOnCartMutation) => void,
    ) {
        setShippingMethodsOnCartMutation({
            refetchQueries: ['CartTotal'],
            variables: {
                input,
            },
            onCompleted: (data: SetShippingMethodsOnCartMutation) => {
                onSuccess(data);
            },
            onError: err => {
                if (error?.message !== INACTIVE_CART_MESSAGE) {
                    toast.error(err.message);
                }

                logSentry({
                    error: err,
                    name: 'cart',
                    transactionName: 'useSetShippingMethodOnCart',
                    extra: {input},
                });
            },
        });
    }

    return {
        setShippingMethod,
        loading,
        error,
    };
}

export function usePlaceOrder() {
    const [placeOrderMutation, {loading, error, data}] = usePlaceOrderMutation();

    function placeOrder(input: PlaceOrderMutationVariables['input'], onError: (err: ApolloError) => void) {
        return placeOrderMutation({
            variables: {
                input,
            },
            context: {
                uri: getMagentoGraphqlApiEndpointPublic(),
            },
            onCompleted: () => {},
            onError: err => {
                if (err.message === INACTIVE_CART_MESSAGE) {
                    err.message = ORDER_ALREADY_PLACED;
                }

                toast.error(err.message);
                logSentry({
                    error: err,
                    name: 'cart',
                    transactionName: 'usePlaceOrder',
                    extra: {input},
                });
                onError(err);
            },
        });
    }

    return {
        placeOrder,
        loading,
        error,
        data,
    };
}

export function useSetGuestEmailOnCart() {
    const [setGuestEmailOnCartMutation, {loading, error}] = useSetGuestEmailOnCartMutation();

    function setGuestEmailOnCart(input: SetGuestEmailOnCartMutationVariables['input']) {
        return setGuestEmailOnCartMutation({
            variables: {
                input,
            },
        });
    }

    return {
        setGuestEmailOnCart,
        loading,
        error,
    };
}

export function useSetBillingAddressesOnCart() {
    const {t} = useTranslation();
    const [setBillingAddressesOnCartMutation, {loading, error}] = useSetBillingAddressesOnCartMutation();

    function setBillingAddress(
        cartId: string,
        sameAsShipping: boolean,
        input: CartAddressInput | undefined,
        customerAddressId: number | null | undefined,
    ) {
        return setBillingAddressesOnCartMutation({
            refetchQueries: ['CartTotal'],
            variables: {
                input: {
                    cart_id: cartId,
                    billing_address: customerAddressId
                        ? {customer_address_id: customerAddressId, same_as_shipping: sameAsShipping}
                        : {
                              address: input
                                  ? {
                                        ...input,
                                        save_in_address_book: false, // handle this field when set shipping address
                                    }
                                  : undefined,
                              customer_address_id: customerAddressId,
                              // same_as_shipping: sameAsShipping,
                          },
                },
            },
            onError: (error: ApolloError) => {
                if (error?.message !== INACTIVE_CART_MESSAGE) {
                    toast.error(t(error?.message), {
                        toastId: 'error',
                    });
                }
                logSentry({
                    error,
                    name: 'cart',
                    transactionName: 'useSetBillingAddressesOnCart',
                    extra: {cartId, sameAsShipping, input, customerAddressId},
                });
            },
        });
    }

    return {
        setBillingAddress,
        loading,
        error,
    };
}

export function useSetShippingAddress() {
    const {t} = useTranslation();
    const [setShippingAddressMutation, {loading, error, data}] = useSetShippingAddressesOnCartMutation({
        onCompleted: () => {},
        onError: (error: ApolloError) => {
            if (error?.message !== INACTIVE_CART_MESSAGE) {
                toast.error(t(error?.message), {
                    toastId: 'error',
                });
            }
            logSentry({
                error,
                name: 'cart',
                transactionName: 'useSetShippingAddress',
                extra: {error},
            });
        },
    });
    const setShippingAddress = (
        cartId: string,
        input: CartAddressInput | undefined,
        customerAddressId: number | null | undefined,
        customerNotes: string | undefined | null,
    ) => {
        // setSelectedShippingMethod(initialSelectedShippingMethodState);
        return setShippingAddressMutation({
            // refetchQueries: ['AvailableShippingMethods', 'CartTotal'],
            variables: {
                input: {
                    cart_id: cartId,
                    shipping_addresses: [
                        customerAddressId
                            ? {customer_address_id: customerAddressId, customer_notes: customerNotes}
                            : {
                                  address: input,
                                  customer_notes: customerNotes,
                              },
                    ],
                },
            },
        });
    };

    return {
        setShippingAddress,
        loading,
        error,
        data,
    };
}

export function useAvailableShippingMethod(cartId: string) {
    const {data, loading, error} = useAvailableShippingMethodsQuery({
        variables: {
            cartId,
        },
        fetchPolicy: 'no-cache',
        onCompleted: () => {},
        onError: err => {
            toast.error(err.message);
        },
    });

    return {data, loading, error};
}

export function useAvailableCoupons() {
    const {data, loading, error} = useAvailableCouponsQuery({
        onCompleted: () => {},
        onError: err => {
            toast.error(err.message);
        },
    });

    return {data, loading, error};
}

export function useAvailablePaymentMethod(cartId: string) {
    const [, setAvailablePaymentMethods] = useAtom(availablePaymentMethodAtom);
    const [, setSelectedPaymentMethod] = useAtom(selectedPaymentMethodAtom);

    const {data, loading, error} = useAvailablePaymentMethodsQuery({
        variables: {
            cartId,
        },
        onCompleted: data => {
            const cart = data.cart;
            setSelectedPaymentMethod(cart?.selected_payment_method as SelectedPaymentMethod);
            setAvailablePaymentMethods(cart?.available_payment_methods as AvailablePaymentMethod[]);
        },
        onError: err => {
            toast.error(err.message);
            logSentry({
                error: err,
                name: 'cart',
                transactionName: 'useAvailablePaymentMethod',
                extra: {cartId},
            });
        },
    });

    return {data, loading, error};
}

export function useApplyCouponToCart() {
    const [applyCouponToCartMutation, {loading, error, data}] = useApplyCouponToCartMutation({
        onCompleted: () => {},
        onError: (error: ApolloError) => {
            logSentry({
                error,
                name: 'cart',
                transactionName: 'useApplyCouponToCart',
                extra: {error},
            });
        },
    });
    const applyCouponToCart = (cartId: string, couponCode: string) => {
        return applyCouponToCartMutation({
            refetchQueries: ['CartTotal', 'CartItems'],
            variables: {
                input: {
                    cart_id: cartId,
                    coupon_code: couponCode,
                },
            },
        });
    };

    return {
        applyCouponToCart,
        loading,
        error,
        data,
    };
}

export function useAppliedCoupons() {
    const [fetchAppliedCouponsQuery, {data, loading, error}] = useGetAppliedCouponsLazyQuery({
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (error) {
            toast.error(error.message);
        }
    }, [error]);

    const fetchAppliedCoupons = async (cartId: string) => {
        const response = await fetchAppliedCouponsQuery({
            variables: {
                cartId,
            },
        });
        return response.data;
    };

    return {fetchAppliedCoupons, data, loading, error};
}

export function useRemoveCouponToCart() {
    const {t} = useTranslation();
    const [removeCouponFromCartMutation, {loading, error, data}] = useRemoveCouponFromCartMutation({
        onCompleted: () => {},
        onError: (error: ApolloError) => {
            toast.error(t(error?.message), {
                toastId: 'error',
            });
        },
    });
    const removeCouponFromCart = (cartId: string) => {
        return removeCouponFromCartMutation({
            refetchQueries: ['CartTotal', 'GetAppliedCoupons'],
            variables: {
                input: {
                    cart_id: cartId,
                },
            },
        });
    };

    return {
        removeCouponFromCart,
        loading,
        error,
        data,
    };
}

// Kicoins
export function useCustomerKicoins() {
    const {data, loading, error} = useCustomerKicoinQuery({
        onCompleted: () => {},
        onError: (err: ApolloError) => {
            toast.error(err.message);
        },
    });

    return {data, loading, error};
}

export function useGetDiscountKicoins(cartId: string) {
    const {data, loading, error} = useGetDiscountKicoinsQuery({
        variables: {
            cartId,
        },
        onCompleted: () => {},
        onError: (err: ApolloError) => {
            toast.error(err.message);
        },
    });

    return {data, loading, error};
}

export function useRemoveKicoinsFromCart() {
    const {t} = useTranslation();
    const [removeKicoinsFromCartMutation, {loading, error, data}] = useRemoveKicoinsFromCartMutation({
        onCompleted: () => {},
        onError: (error: ApolloError) => {
            toast.error(t(error?.message), {
                toastId: 'error',
            });
        },
    });
    const removeKicoinsFromCart = (cartId: string) => {
        return removeKicoinsFromCartMutation({
            refetchQueries: ['CartTotal', 'GetDiscountKicoins'],
            variables: {
                input: {
                    cart_id: cartId,
                },
            },
        });
    };

    return {
        removeKicoinsFromCart,
        loading,
        error,
        data,
    };
}

export function useApplyKicoinsToCart() {
    const {t} = useTranslation();
    const [applyKicoinsToCartMutation, {loading, error, data}] = useApplyKicoinsToCartMutation({
        onCompleted: () => {},
        onError: (error: ApolloError) => {
            toast.error(t(error?.message), {
                toastId: 'error',
            });
        },
    });
    const applyKicoinsToCart = (cartId: string) => {
        return applyKicoinsToCartMutation({
            refetchQueries: ['CartTotal', 'GetDiscountKicoins'],
            variables: {
                input: {
                    cart_id: cartId,
                },
            },
        });
    };

    return {
        applyKicoinsToCart,
        loading,
        error,
        data,
    };
}

export function useMergeCart() {
    const [mergeCartsMutation, {loading, error, data}] = useMergeCartsMutation();
    const mergeCarts = useCallback(
        async (sourceCartId: string, destinationCartId: string) => {
            const result = await mergeCartsMutation({
                variables: {
                    sourceCartId,
                    destinationCartId,
                },
            });
            return result?.data?.mergeCarts?.id;
        },
        [mergeCartsMutation],
    );

    return {
        mergeCarts,
        loading,
        error,
        data,
    };
}
